import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Quote,
  Type,
  Underline,
} from "lucide-react";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { sanitizeRichTextHtml } from "@/components/terms/rich-text-utils";

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  minHeightClassName?: string;
  insertTextRequest?: { id: string; text: string } | null;
  onInsertTextApplied?: (id: string) => void;
};

const FONT_SIZE_STEP_PX = 1;
const MIN_FONT_SIZE_PX = 10;
const MAX_FONT_SIZE_PX = 48;
const DEFAULT_LINE_HEIGHT_RATIO = 1.5;
const MULTI_LINE_TAGS = new Set(["BR", "DIV", "P", "LI", "UL", "OL", "BLOCKQUOTE", "H1", "H2"]);

type Command = { key: string; icon: ComponentType<{ className?: string }>; run: () => void };

const COMMANDS: Command[] = [
  { key: "paragraph", icon: Type, run: () => document.execCommand("formatBlock", false, "p") },
  { key: "heading1", icon: Heading1, run: () => document.execCommand("formatBlock", false, "h1") },
  { key: "heading2", icon: Heading2, run: () => document.execCommand("formatBlock", false, "h2") },
  { key: "bold", icon: Bold, run: () => document.execCommand("bold") },
  { key: "italic", icon: Italic, run: () => document.execCommand("italic") },
  { key: "underline", icon: Underline, run: () => document.execCommand("underline") },
  { key: "list", icon: List, run: () => document.execCommand("insertUnorderedList") },
  { key: "numberedList", icon: ListOrdered, run: () => document.execCommand("insertOrderedList") },
  { key: "quote", icon: Quote, run: () => document.execCommand("formatBlock", false, "blockquote") },
  { key: "alignLeft", icon: AlignLeft, run: () => document.execCommand("justifyLeft") },
  { key: "alignCenter", icon: AlignCenter, run: () => document.execCommand("justifyCenter") },
  { key: "alignRight", icon: AlignRight, run: () => document.execCommand("justifyRight") },
];

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Digite aqui...",
  className,
  minHeightClassName = "min-h-[220px]",
  insertTextRequest,
  onInsertTextApplied,
}: RichTextEditorProps) => {
  const { t } = useTranslation("prescriptions");
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastAppliedInsertIdRef = useRef<string | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const normalizedValue = useMemo(() => sanitizeRichTextHtml(value), [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== normalizedValue) {
      el.innerHTML = normalizedValue;
    }
  }, [normalizedValue]);

  useEffect(() => {
    if (!insertTextRequest || !editorRef.current) return;
    if (lastAppliedInsertIdRef.current === insertTextRequest.id) return;

    lastAppliedInsertIdRef.current = insertTextRequest.id;

    editorRef.current.focus();
    const success = document.execCommand("insertText", false, insertTextRequest.text);
    if (!success) {
      const current = editorRef.current.innerHTML ?? "";
      editorRef.current.innerHTML = `${current}${insertTextRequest.text}`;
    }
    handleInput();
    onInsertTextApplied?.(insertTextRequest.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertTextRequest, onInsertTextApplied]);

  const handleInput = () => {
    const html = editorRef.current?.innerHTML ?? "";
    onChange(sanitizeRichTextHtml(html));
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    const editor = editorRef.current;
    if (!selection || !editor || selection.rangeCount === 0) {
      savedRangeRef.current = null;
      return;
    }

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = null;
      return;
    }
    savedRangeRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    const range = savedRangeRef.current;
    const editor = editorRef.current;
    if (!selection || !range || !editor || !editor.contains(range.commonAncestorContainer)) return;

    selection.removeAllRanges();
    selection.addRange(range);
    return range;
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      saveSelection();
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  });

  const getClosestElement = (node: Node | null) => {
    if (!node) return null;
    return node instanceof Element ? node : node.parentElement;
  };

  const getCurrentFontSize = () => {
    const selection = window.getSelection();
    const anchorElement = getClosestElement(selection?.anchorNode ?? null) ?? editorRef.current;
    const fontSize = Number.parseFloat(window.getComputedStyle(anchorElement as Element).fontSize);
    return Number.isFinite(fontSize) ? fontSize : 16;
  };

  const getNormalizedFontSize = (fontSizePx: number) =>
    Math.min(MAX_FONT_SIZE_PX, Math.max(MIN_FONT_SIZE_PX, Math.round(fontSizePx)));

  const getComputedLineHeightRatio = (element: Element, fontSizePx: number) => {
    const lineHeight = window.getComputedStyle(element).lineHeight;
    const parsed = Number.parseFloat(lineHeight);
    if (Number.isFinite(parsed) && fontSizePx > 0) {
      return parsed / fontSizePx;
    }
    return DEFAULT_LINE_HEIGHT_RATIO;
  };

  const applyTypographyToElement = (element: HTMLElement, fontSizePx: number, lineHeightRatio?: number) => {
    const normalizedSize = getNormalizedFontSize(fontSizePx);
    const ratio = lineHeightRatio ?? getComputedLineHeightRatio(element, normalizedSize);
    const normalizedLineHeight = Number((normalizedSize * ratio).toFixed(1));
    element.style.fontSize = `${normalizedSize}px`;
    element.style.lineHeight = `${normalizedLineHeight}px`;
  };

  const selectionContainsMultipleLines = (range: Range) => {
    const fragment = range.cloneContents();
    const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT);
    let currentNode = walker.nextNode();
    while (currentNode) {
      if (currentNode instanceof HTMLElement && MULTI_LINE_TAGS.has(currentNode.tagName)) {
        return true;
      }
      currentNode = walker.nextNode();
    }
    return false;
  };

  const getFormattingTarget = (element: Element | null) => {
    const editor = editorRef.current;
    if (!element || !editor || !editor.contains(element)) return null;

    let current: Element | null = element;
    while (current && current.parentElement && current.parentElement !== editor) {
      if (MULTI_LINE_TAGS.has(current.tagName)) {
        return current as HTMLElement;
      }
      current = current.parentElement;
    }

    return (element === editor ? null : element) as HTMLElement | null;
  };

  const hasExplicitTypographyStyle = (element: HTMLElement) => {
    const inlineFontSize = element.style.fontSize?.trim();
    const inlineLineHeight = element.style.lineHeight?.trim();
    return Boolean(inlineFontSize || inlineLineHeight);
  };

  const getTypographyTarget = (element: Element | null) => {
    const editor = editorRef.current;
    if (!element || !editor || !editor.contains(element)) return null;

    let current: Element | null = element;
    while (current && current !== editor) {
      if (current instanceof HTMLElement && hasExplicitTypographyStyle(current)) {
        return current;
      }
      current = current.parentElement;
    }

    return getFormattingTarget(element);
  };

  const splitRangeBoundaries = (range: Range) => {
    let startContainer = range.startContainer;
    let startOffset = range.startOffset;
    let endContainer = range.endContainer;
    let endOffset = range.endOffset;

    if (endContainer.nodeType === Node.TEXT_NODE) {
      const textNode = endContainer as Text;
      if (endOffset > 0 && endOffset < textNode.data.length) {
        textNode.splitText(endOffset);
      }
    }

    if (startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = startContainer as Text;
      if (startOffset > 0 && startOffset < textNode.data.length) {
        const originalStartOffset = startOffset;
        const splitNode = textNode.splitText(startOffset);
        startContainer = splitNode;
        startOffset = 0;
        if (endContainer === textNode) {
          endContainer = splitNode;
          endOffset = endOffset - originalStartOffset;
        }
      }
    }

    const nextRange = document.createRange();
    nextRange.setStart(startContainer, startOffset);
    nextRange.setEnd(endContainer, endOffset);
    return nextRange;
  };

  const adjustSelectedTextNodes = (range: Range, delta: number) => {
    const editor = editorRef.current;
    if (!editor) return false;

    const adjustedRange = splitRangeBoundaries(range);
    const walker = document.createTreeWalker(
      adjustedRange.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;
          if (!editor.contains(node)) return NodeFilter.FILTER_REJECT;
          return adjustedRange.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      },
    );

    const textNodes: Text[] = [];
    let currentNode = walker.nextNode();
    while (currentNode) {
      if (currentNode instanceof Text) {
        textNodes.push(currentNode);
      }
      currentNode = walker.nextNode();
    }

    if (textNodes.length === 0) return false;

    const wrappedNodes: HTMLElement[] = [];

    textNodes.forEach((textNode) => {
      const parentElement = textNode.parentElement;
      if (!parentElement) return;

      const typographyTarget = getTypographyTarget(parentElement) ?? parentElement;
      const currentFontSize = getCurrentFontSizeForElement(typographyTarget);
      const ratio = getComputedLineHeightRatio(typographyTarget, currentFontSize);
      const span = document.createElement("span");
      const normalizedSize = getNormalizedFontSize(currentFontSize + delta);
      const lineHeight = Number((normalizedSize * ratio).toFixed(1));
      span.style.fontSize = `${normalizedSize}px`;
      span.style.lineHeight = `${lineHeight}px`;
      span.textContent = textNode.textContent ?? "";
      textNode.parentNode?.replaceChild(span, textNode);
      wrappedNodes.push(span);
    });

    if (wrappedNodes.length === 0) return false;

    const selection = window.getSelection();
    if (selection) {
      const nextRange = document.createRange();
      nextRange.setStartBefore(wrappedNodes[0]);
      nextRange.setEndAfter(wrappedNodes[wrappedNodes.length - 1]);
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }

    return true;
  };

  const adjustSelectedBlocks = (range: Range, delta: number) => {
    const editor = editorRef.current;
    if (!editor) return false;

    const targets = new Set<HTMLElement>();
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();

    while (currentNode) {
      if (
        currentNode.textContent?.trim() &&
        range.intersectsNode(currentNode)
      ) {
        const target = getTypographyTarget(getClosestElement(currentNode));
        if (target) targets.add(target);
      }
      currentNode = walker.nextNode();
    }

    if (targets.size === 0) return false;

    targets.forEach((target) => {
      const currentFontSize = getCurrentFontSizeForElement(target);
      const ratio = getComputedLineHeightRatio(target, currentFontSize);
      applyTypographyToElement(target, currentFontSize + delta, ratio);
    });

    return true;
  };

  const getCurrentFontSizeForElement = (element: Element) => {
    const fontSize = Number.parseFloat(window.getComputedStyle(element).fontSize);
    return Number.isFinite(fontSize) ? fontSize : 16;
  };

  const adjustSingleBlockSelection = (range: Range, delta: number) => {
    const startTarget = getTypographyTarget(getClosestElement(range.startContainer));
    const endTarget = getTypographyTarget(getClosestElement(range.endContainer));

    if (!startTarget || !endTarget || startTarget !== endTarget) {
      return false;
    }

    const currentFontSize = getCurrentFontSizeForElement(startTarget);
    const ratio = getComputedLineHeightRatio(startTarget, currentFontSize);
    applyTypographyToElement(startTarget, currentFontSize + delta, ratio);
    return true;
  };

  const applyFontSizeDeltaToSelection = (delta: number) => {
    const selection = window.getSelection();
    let range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !editorRef.current?.contains(range.commonAncestorContainer)) {
      range = restoreSelection() ?? null;
    }
    if (!selection || !range || !editorRef.current || !editorRef.current.contains(range.commonAncestorContainer)) {
      return;
    }

    if (range.collapsed) {
      const focusElement = getClosestElement(selection.anchorNode);
      if (focusElement && focusElement !== editorRef.current) {
        const target = getTypographyTarget(focusElement) ?? (focusElement as HTMLElement);
        const currentFontSize = getCurrentFontSizeForElement(target);
        applyTypographyToElement(
          target,
          currentFontSize + delta,
          getComputedLineHeightRatio(target, currentFontSize),
        );
      } else {
        const normalizedSize = getNormalizedFontSize(getCurrentFontSize() + delta);
        const lineHeight = Number((normalizedSize * DEFAULT_LINE_HEIGHT_RATIO).toFixed(1));
        document.execCommand(
          "insertHTML",
          false,
          `<span style="font-size: ${normalizedSize}px; line-height: ${lineHeight}px;">&#8203;</span>`,
        );
      }
      return;
    }

    if (adjustSelectedTextNodes(range, delta)) {
      saveSelection();
      return;
    }

    const focusElement = getClosestElement(selection.anchorNode) ?? editorRef.current;
    const targetElement = getTypographyTarget(focusElement) ?? (focusElement as HTMLElement);
    const currentFontSize = getCurrentFontSizeForElement(targetElement);
    const ratio = getComputedLineHeightRatio(
      targetElement,
      currentFontSize,
    );
    const normalizedSize = getNormalizedFontSize(currentFontSize + delta);
    const lineHeight = Number((normalizedSize * ratio).toFixed(1));

    const extracted = range.extractContents();
    const span = document.createElement("span");
    span.style.fontSize = `${normalizedSize}px`;
    span.style.lineHeight = `${lineHeight}px`;
    span.appendChild(extracted);
    range.insertNode(span);
    selection.removeAllRanges();
    const nextRange = document.createRange();
    nextRange.selectNodeContents(span);
    selection.addRange(nextRange);
    saveSelection();
  };

  const resetParagraphTypography = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const target = getCurrentFormattingTarget();
    if (!target || !editor.contains(target)) return;

    let paragraph = target as HTMLElement;

    if (paragraph.tagName !== "P") {
      const newParagraph = document.createElement("p");
      while (paragraph.firstChild) {
        newParagraph.appendChild(paragraph.firstChild);
      }
      paragraph.parentNode?.replaceChild(newParagraph, paragraph);
      paragraph = newParagraph;
    }

    const baseFontSize = 14;
    const lineHeightPx = Number((baseFontSize * DEFAULT_LINE_HEIGHT_RATIO).toFixed(1));
    paragraph.style.fontSize = `${baseFontSize}px`;
    paragraph.style.lineHeight = `${lineHeightPx}px`;
    paragraph.style.margin = "0";

    const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_ELEMENT);
    let current = walker.nextNode();
    while (current) {
      if (current instanceof HTMLElement) {
        current.style.fontSize = "";
        current.style.lineHeight = "";
        if (current !== paragraph) {
          current.style.margin = "";
        }
      }
      current = walker.nextNode();
    }
  };

  const executeCommand = (command: Command) => {
    restoreSelection();
    command.run();

    if (command.key === "paragraph") {
      resetParagraphTypography();
    }

    handleInput();
    saveSelection();
    editorRef.current?.focus();
  };

  const getCurrentFormattingTarget = () => {
    const selection = window.getSelection();
    const editor = editorRef.current;
    if (!selection || !editor) return null;

    const range =
      selection.rangeCount > 0 && editor.contains(selection.getRangeAt(0).commonAncestorContainer)
        ? selection.getRangeAt(0)
        : restoreSelection();

    if (!range) return null;

    return getTypographyTarget(getClosestElement(range.startContainer))
      ?? getClosestElement(selection.anchorNode)
      ?? editor;
  };

  const adjustFontSize = (delta: number) => {
    restoreSelection();
    const selection = window.getSelection();
    const range =
      selection?.rangeCount && editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer)
        ? selection.getRangeAt(0)
        : savedRangeRef.current;

    if (!range || range.collapsed) {
      return;
    }

    applyFontSizeDeltaToSelection(delta);
    handleInput();
    saveSelection();
    editorRef.current?.focus();
  };

  return (
    <div className={cn("min-w-0 overflow-hidden rounded-lg border", className)}>
      <div className="flex max-w-full gap-1 overflow-x-auto rounded-t-lg border-b bg-muted/40 p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {COMMANDS.map((command) => {
          const Icon = command.icon;
          const label = t(`richTextEditor.${command.key}`);
          return (
            <Button
              key={command.key}
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => executeCommand(command)}
              aria-label={label}
              title={label}
            >
              <Icon className="h-4 w-4" />
            </Button>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 px-2 text-xs font-semibold"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => adjustFontSize(-FONT_SIZE_STEP_PX)}
          aria-label={t("richTextEditor.decreaseFontSize")}
          title={t("richTextEditor.decreaseFontSize")}
        >
          A-
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 px-2 text-xs font-semibold"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => adjustFontSize(FONT_SIZE_STEP_PX)}
          aria-label={t("richTextEditor.increaseFontSize")}
          title={t("richTextEditor.increaseFontSize")}
        >
          A+
        </Button>
      </div>

      <div
        ref={editorRef}
        className={cn(
          "w-full px-3 py-3 text-[14px] outline-none [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0 [&_li]:my-0 [&_blockquote]:mt-2 [&_blockquote]:mb-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:text-lg [&_h2]:font-semibold",
          minHeightClassName,
        )}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        data-placeholder={placeholder || t("richTextEditor.placeholder")}
      />
    </div>
  );
};
